import React, { useEffect, useState } from "react";
import { useTimeSheetStore } from "../../../../config/stores";
import usePrevious from "../../../../components/hooks/usePrevious";
import dayjs from "dayjs";
import AppButton from "../../../../components/common/AppButton";
import AntdModal from "../../../../components/common/AntdModal";
import { Form, message } from "antd";
import AppInput from "../../../../components/common/AppInput";
import axios from "axios";

const TimeSheetActivitiesForm = () => {
  const timeSheetsStore = useTimeSheetStore();
  const {
    addingTimeSheetActivity,
    createdTimeSheetActivity,
    timeSheetActivityToEdit,
    showAddingTimeSheetActivity,
    timeSheetDetails,
    timeSheetActivitiesDate,
  } = timeSheetsStore;
  const [form] = Form.useForm();
  const [publicHolidays, setPublicHolidays] = useState([]);
  const prevState = usePrevious({ createdTimeSheetActivity });

  const isShared = Form.useWatch("is_shared", form);

  useEffect(() => {
    const fetchPublicHolidays = async () => {
      try {
        const res = await axios.get("/settings/public-holidays");
        let holidaysData = [];
        if (Array.isArray(res)) {
          holidaysData = res;
        } else if (Array.isArray(res?.data)) {
          holidaysData = res.data;
        } else if (res?.data?.data && Array.isArray(res.data.data)) {
          holidaysData = res.data.data;
        } else if (res?.data && typeof res.data === 'object') {
          holidaysData = res.data.data || [];
        }
        setPublicHolidays(holidaysData);
      } catch (error) {
        console.error("Failed to fetch public holidays");
      }
    };
    fetchPublicHolidays();
  }, []);

  const closeModal = () => {
    timeSheetsStore.setAddingTimeSheetActivity();
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((data) => {
        const activityPayload = {
          ...data,
          day_of_month: data.date.date(),
          date: data.date.format("YYYY-MM-DD"),
        };

        if (addingTimeSheetActivity) {
          const payload = {
            activities: [activityPayload],
            timeSheetId: timeSheetDetails.id,
          };
          timeSheetsStore.createTimeSheetActivity(payload, timeSheetDetails.id);
        } else {
          timeSheetsStore.updateTimeSheetActivity(
            activityPayload,
            timeSheetActivityToEdit.id,
            timeSheetDetails.id
          );
        }
      })
      .catch((err) => {
        message.error("Please fill in all fields");
      });
  };

  useEffect(() => {
    if (
      prevState &&
      createdTimeSheetActivity &&
      prevState.createdTimeSheetActivity !== createdTimeSheetActivity
    ) {
      message.success("Activity added.");
      timeSheetsStore.setAddingTimeSheetActivity();
    }
  }, [prevState, createdTimeSheetActivity, timeSheetsStore]);

  useEffect(() => {
    if (addingTimeSheetActivity) {
      const sheetMonth = String(timeSheetDetails.month).padStart(2, "0");
      const monthStart = dayjs(`${timeSheetDetails.year}-${sheetMonth}-01`);
      let defaultDate = monthStart;

      if (timeSheetActivitiesDate?.day) {
        defaultDate = monthStart.date(timeSheetActivitiesDate.day);
      } else {
        while (defaultDate.day() === 0 || defaultDate.day() === 6) {
          defaultDate = defaultDate.add(1, "day");
        }
      }

      form.setFieldsValue({
        date: defaultDate,
      });
    } else {
      const editDate = timeSheetActivityToEdit.day_of_month
        ? dayjs(
            `${timeSheetDetails.year}-${String(timeSheetDetails.month).padStart(2, "0")}-${String(
              timeSheetActivityToEdit.day_of_month
            ).padStart(2, "0")}`
          )
        : dayjs(timeSheetActivityToEdit.date);

      form.setFieldsValue({
        date: editDate,
        activity: timeSheetActivityToEdit.activity,
        is_shared: timeSheetActivityToEdit.is_shared,
        project_id: timeSheetActivityToEdit.project_id,
        hours: timeSheetActivityToEdit.hours,
      });
    }
  }, [
    addingTimeSheetActivity,
    timeSheetActivityToEdit,
    timeSheetActivitiesDate,
    timeSheetDetails,
    form,
  ]);

  const currentMonth =
    String(timeSheetDetails).length > 1
      ? `${timeSheetDetails.month}`.length > 1
        ? timeSheetDetails.month
        : `0${timeSheetDetails.month}`
      : timeSheetDetails.month;

  const currentPeriod = dayjs(
    `${currentMonth}/${timeSheetDetails.year}`,
    "MM/YYYY"
  );
  const startOfMonth = currentPeriod.startOf("month");
  const endOfMonth = currentPeriod.endOf("month");

  // Only restrict to the timesheet's own month — weekends and public holidays are still selectable
  const disabledDate = (currentDate) => {
    return currentDate.isBefore(startOfMonth) || currentDate.isAfter(endOfMonth);
  };

  return (
    <div>
      <AntdModal
        title={
          addingTimeSheetActivity
            ? "ADD TIME SHEET ACTIVITY"
            : "EDIT TIME SHEET ACTIVITY"
        }
        visible={showAddingTimeSheetActivity}
        closeModal={closeModal}
        size="large"
        footer={[
          <AppButton
            text={
              addingTimeSheetActivity
                ? "ADD TIME SHEET ACTIVITY"
                : "EDIT TIME SHEET ACTIVITY"
            }
            key={"create"}
            loading={
              timeSheetsStore.creatingTimeSheetActivity ||
              timeSheetsStore.updatingTimeSheetActivity
            }
            onClick={onSubmit}
          />,
          <AppButton
            text="CANCEL"
            key={"cancel"}
            onClick={closeModal}
            danger
          />,
        ]}
        zIndex={1000}
      >
        <Form
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 18 }}
          layout="horizontal"
          form={form}
        >
          <AppInput
            type="date"
            label="Date"
            name="date"
            rules={[{ required: true }]}
            inputAttributes={{
              disabledDate,
            }}
          />
          <AppInput
            type="select"
            label="Is Shared ?"
            name="is_shared"
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            rules={[{ required: true }]}
          />
          {!isShared && (
            <>
              <AppInput
                type="select"
                label="Project"
                name="project_id"
                options={timeSheetDetails.projects?.map((project) => ({
                  label: project.name,
                  value: project.id,
                }))}
                rules={[{ required: true }]}
              />
            </>
          )}

          <AppInput
            name="hours"
            type="number"
            label="Hours"
            rules={[{ required: true }]}
            inputAttributes={{
              addonAfter: "Hr(s)",
              min: 1,
              max: 24,
            }}
          />
          <AppInput
            name="activity"
            type="textarea"
            label="Activity"
            rules={[{ required: true }]}
          />
        </Form>
      </AntdModal>
    </div>
  );
};

export default TimeSheetActivitiesForm;
